import { useWishlist as coreUseWishlist } from "@shopware/composables";
import type { Schemas } from '#shopware'
import { ApiClientError } from '@shopware/api-client'

export function useWishlist() {
    // Global state for loaded wishlist products using useState for SSR safety
    const wishlistProducts = useState<Schemas['Product'][]>('wishlistProducts', () => [])
    const isLoadingProducts = useState<boolean>('isLoadingWishlistProducts', () => false)

    const coreFunctionality = coreUseWishlist();
    const { apiClient } = useShopwareContext()
    const { resolveApiErrors } = useApiErrorsResolver()
    const { pushError } = useNotifications()

    /**
     * Load detailed product information for all wishlist items
     * Fetches product data with only necessary fields for display in ProductListItem
     */
    const loadWishlistProducts = async (): Promise<void> => {
        if (coreFunctionality.items.value.length === 0) {
            wishlistProducts.value = []
            return
        }

        isLoadingProducts.value = true
        try {
            const result = await apiClient.invoke('readProduct post /product', {
                body: {
                    filter: [
                        {
                            type: 'equalsAny',
                            field: 'id',
                            value: coreFunctionality.items.value.join('|')
                        }
                    ],
                    limit: coreFunctionality.items.value.length,
                    associations: {
                        media: {},
                        cover: { associations: { media: {} } },
                        options: { associations: { group: {} } },
                        properties: { associations: { group: {} } },
                        seoUrls: {} 
                    },
                    includes: {
                        product: [
                            'id',
                            'name',
                            'translated',
                            'calculatedPrice',
                            'calculatedPrices',
                            'calculatedCheapestPrice',
                            'cover',
                            'options',
                            'properties',
                            'seoUrls'
                        ],
                        product_media: [
                            'id',
                            'media'
                        ],
                        media: [
                            'id',
                            'url',
                            'alt',
                            'title'
                        ],
                        property_group_option: [
                            'id',
                            'name',
                            'translated',
                            'group',
                            'groupId'
                        ],
                        property_group: [
                            'id',
                            'name',
                            'translated'
                        ],
                        seo_url: [
                            'seoPathInfo'
                        ]
                    }
                }
            })
            wishlistProducts.value = result.data.elements || []
        } catch (error) {
            handleError(error)
            wishlistProducts.value = []
        } finally {
            isLoadingProducts.value = false
        }
    }

    const handleError = (e: unknown) => {
        if (e instanceof ApiClientError) {
            const _errors = resolveApiErrors(e.details.errors)
            for (const error of _errors) {
                pushError(error)
            }
        }
    }

    return {
        ...coreFunctionality,
        loadWishlistProducts,
        wishlistProducts: computed(() => wishlistProducts.value),
        isLoadingProducts: computed(() => isLoadingProducts.value)
    };
}